import { Grid, makeStyles } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import type { TermsOption } from '@paintscout/util/builder'
import { getObjectLabels, updateTermsOption } from '@paintscout/util/builder'
import type { StyleClasses, DialogProps } from '@paintscout/ui'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Editor,
  FormControlLabel,
  IconButton,
  InputField,
  ActiveSwitch,
  Typography,
  useUser
} from '@paintscout/ui'
import CloseIcon from '@material-ui/icons/Close'
import clone from 'lodash/clone'
import type { OptionsDocument } from 'paintscout'
import React, { useMemo, useRef, useState } from 'react'
import { useFlags } from 'shared/components/common/FlagsProvider'

import QuoteTypeSelect from '../../QuoteTypeSelect'

export interface EditTermsOptionDialogProps extends DialogProps {
  classes?: DialogProps['classes'] & StyleClasses<typeof useStyles>
  item: TermsOption
  options: OptionsDocument
  onConfirm: (options: OptionsDocument, isDirty?: boolean) => void
  onCancel: () => void
}

export interface EditTermsOptionDialogState {
  item: TermsOption
  isDirty?: boolean
}
const useStyles = makeStyles<Theme, EditTermsOptionDialogProps>(
  (theme) => ({
    root: {},
    wrapper: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    },
    editor: {
      flexGrow: 1
    },
    divider: {},
    content: {
      minHeight: `400px`
    },
    htmlContent: {
      padding: theme.spacing(),
      border: '2px solid #000'
    },
    disabledMessage: {
      marginBottom: theme.spacing(2)
    },
    dialogTitle: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    switchLabel: {
      marginLeft: '0 !important',
      marginRight: 0
    },
    dialogContent: {
      maxHeight: '70vh',
      overflowY: 'auto',
      [theme.breakpoints.down('sm')]: {
        maxHeight: '75vh'
      }
    }
  }),
  { name: 'EditTermsOptionDialog' }
)

export default function EditTermsOptionDialog({
  loading,
  options,
  onConfirm,
  onCancel,
  item: propsItem,
  ...otherProps
}: EditTermsOptionDialogProps) {
  const classes = useStyles({ loading, onConfirm, onCancel, item: propsItem, options, ...otherProps })
  const [item, setItem] = useState(clone(propsItem))
  const [isDirty, setIsDirty] = useState(false)

  const objectLabels = useMemo(() => getObjectLabels({ options }), [])

  const contentRef = useRef(null)

  const user = useUser()
  const { canDeactivateSettingsItems } = useFlags()

  const termsDisabled = item.disabled || (options.options?.corporate?.disableTermsEditing && !user.isCorporate)

  return (
    <Dialog className={classes.root} maxWidth={'md'} fullWidth={true} {...otherProps}>
      <DialogTitle
        classes={{ root: classes.dialogTitle }}
        rightContent={
          <IconButton size="small" onClick={onCancel} disabled={loading}>
            <CloseIcon />
          </IconButton>
        }
      >
        {'Edit Terms'}
      </DialogTitle>
      <DialogContent classes={{ root: classes.dialogContent }} className={classes.content}>
        <form
          id="terms-option-form"
          onSubmit={(e) => {
            e.preventDefault()
            handleConfirm(e)
          }}
        >
          <Grid container={true} spacing={3}>
            <Grid item={true} xs={12} sm={12} md={6}>
              <InputField
                label={'Label'}
                name={'label'}
                value={item.label}
                fullWidth={true}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item={true} xs={12} sm={12} md={6}>
              <QuoteTypeSelect
                label={`${objectLabels.quote.value} Types`}
                variant="multi"
                onChange={handleQuoteTypeChange}
                value={item.quoteTypes}
                placeholder={{
                  plural: `${objectLabels.quote.value} Types`
                }}
              />
            </Grid>
          </Grid>
          <Grid container={true} spacing={3}>
            <Grid item={true} xs={12} sm={12} md={12}>
              <div className={classes.wrapper}>
                {item.disabled && (
                  <Typography variant={'body2'} classes={{ root: classes.disabledMessage }}>
                    These terms have been customized to include images, videos, or other content not editable here.
                    Please contact us for assistance editing these terms.
                  </Typography>
                )}
                <Editor
                  key={item.name}
                  disabled={termsDisabled}
                  label={'Terms Content'}
                  sublabel={'Make changes this terms template.'}
                  content={item.content}
                  fullWidth={true}
                  ref={contentRef}
                  toolbar={{
                    headings: true,
                    align: true
                  }}
                />
              </div>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions
        showBorder={true}
        leftButton={
          canDeactivateSettingsItems ? (
            <FormControlLabel
              className={classes.switchLabel}
              control={
                <ActiveSwitch
                  color="primary"
                  checked={typeof item.active !== 'undefined' ? !!item.active : true}
                  onChange={handleToggle}
                />
              }
              label="Active"
              labelPlacement="end"
            />
          ) : undefined
        }
      >
        <Button type="submit" form="terms-option-form" disabled={loading} variant={'contained'}>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  )

  function handleChange(event: any) {
    const { name, value } = event.target
    setItem({
      ...item,
      [name]: value
    })
    setIsDirty(true)
  }

  function handleConfirm(_event: any) {
    const updatedTerms = updateTermsOption({
      options,
      termsOption: { ...item, content: contentRef.current?.getHTML() }
    })
    const confirmIsDirty = isDirty || contentRef.current?.isDirty()
    onConfirm(updatedTerms, confirmIsDirty)
  }

  function handleQuoteTypeChange(values: string[]) {
    setItem({
      ...item,
      quoteTypes: values
    })
    setIsDirty(true)
  }
  function handleToggle(_event: React.ChangeEvent<HTMLElement>, _checked: boolean) {
    setItem({
      ...item,
      active: !item.active
    })
    setIsDirty(true)
  }
}
